import type { TFunction } from "i18next";
import { snakeCaseToSentenceCase } from "../../../utils/formatText";
import type { TSelector } from "../../../i18n/types";

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export type TFn = TFunction<any, any>;

// Metric accessors used inside the rule summary. Unknown accessors fall back
// to `snakeCaseToSentenceCase`.
export type MetricKey =
  | "acos"
  | "roas"
  | "ad_spend"
  | "conv_rate"
  | "ctr"
  | "clicks"
  | "bid"
  | "impressions"
  | "cpc"
  | "serving_hours"
  | "ad.serving_hours"
  | "units"
  | "bsr"
  | "tacos"
  | "troas"
  | "click_thru_rate"
  | "cost"
  | "ecpc"
  | "total_detailed_page_view_rate"
  | "total_add_to_cart_rate"
  | "new_to_brand_detail_page_view_rate"
  | "total_new_to_brand_dpv_rate"
  | "brand_search_rate"
  | "budget_percent"
  | "ad.budget_percent"
  | "impression_trend"
  | "clicks_trend"
  | "conversions_trend"
  | "ad_sales"
  | "ad_sales_trend"
  | "units_sold"
  | "conversions"
  | "target_match_types"
  | "keyword_match_types";

const METRIC_LABEL_SELECTORS: Record<MetricKey, TSelector> = {
  target_match_types: ($) => $.programs.metric_label["targets"],
  keyword_match_types: ($) => $.programs.metric_label["keywords"],
  conversions: ($) => $.programs.metric_label["conversions"],
  acos: ($) => $.programs.metric_label["ACOS"],
  roas: ($) => $.programs.metric_label["ROAS"],
  ad_spend: ($) => $.programs.metric_label["ad spend"],
  conv_rate: ($) => $.programs.metric_label["conversion rate"],
  ctr: ($) => $.programs.metric_label["click-through rate"],
  clicks: ($) => $.programs.metric_label["clicks"],
  bid: ($) => $.programs.metric_label["current bid"],
  impressions: ($) => $.programs.metric_label["impressions"],
  cpc: ($) => $.programs.metric_label["cost-per-click"],
  serving_hours: ($) => $.programs.metric_label["serving hours"],
  "ad.serving_hours": ($) => $.programs.metric_label["serving hours"],
  units: ($) => $.programs.metric_label["units"],
  bsr: ($) => $.programs.metric_label["average BSR"],
  tacos: ($) => $.programs.metric_label["total ACOS"],
  troas: ($) => $.programs.metric_label["total ROAS"],
  click_thru_rate: ($) => $.programs.metric_label["click-through-rate"],
  cost: ($) => $.programs.metric_label["cost"],
  ecpc: ($) => $.programs.metric_label["eCPC"],
  total_detailed_page_view_rate: ($) => $.programs.metric_label["total DPV rate"],
  total_add_to_cart_rate: ($) => $.programs.metric_label["total ATC rate"],
  new_to_brand_detail_page_view_rate: ($) => $.programs.metric_label["NTB DPV rate"],
  total_new_to_brand_dpv_rate: ($) => $.programs.metric_label["total NTB DPV rate"],
  brand_search_rate: ($) => $.programs.metric_label["brand search rate"],
  budget_percent: ($) => $.programs.metric_label["spend % of budget"],
  "ad.budget_percent": ($) => $.programs.metric_label["spend % of budget"],
  impression_trend: ($) => $.programs.metric_label["impression trend %"],
  clicks_trend: ($) => $.programs.metric_label["clicks trend %"],
  conversions_trend: ($) => $.programs.metric_label["conversions trend %"],
  ad_sales: ($) => $.programs.metric_label["ad sales"],
  ad_sales_trend: ($) => $.programs.metric_label["ad sales trend %"],
  units_sold: ($) => $.programs.metric_label["units sold"],
};

export const getMetricLabel = (t: TFn, key: string): string =>
  key in METRIC_LABEL_SELECTORS ? t(METRIC_LABEL_SELECTORS[key as MetricKey]) : snakeCaseToSentenceCase(key);

// Grammatical number for the linking verb in the summary ("is" vs "are").
// Defaults to "singular" — replaces the trailing-`s` + pluralBlacklist heuristic.
const METRIC_NUMBER: Partial<Record<MetricKey, "singular" | "plural">> = {
  conversions: "plural",
  clicks: "plural",
  impressions: "plural",
  units: "plural",
  units_sold: "plural",
  target_match_types: "plural",
  keyword_match_types: "plural",
};

export const getMetricNumber = (key: string): "singular" | "plural" => METRIC_NUMBER[key as MetricKey] ?? "singular";

// Backend action enum → summary verb selector. Covers the actions that surface
// in bidding/lineitem_bidding/tagging engines (the engines that render a rule
// summary). Unknown actions fall back to `snakeCaseToSentenceCase`.
export type ActionKey =
  | "USE_ALGO"
  | "DO_NOTHING"
  | "PAUSE"
  | "SET_BID_CPC"
  | "ADD_TAG"
  | "INCREASE_BID"
  | "LOWER_BID"
  | "SET_BID"
  | "INCREASE_BASE_BID"
  | "LOWER_BASE_BID"
  | "SET_BASE_BID"
  | "INCREASE_PRICE"
  | "LOWER_PRICE"
  | "SET_PRICE"
  | "INCREASE_BUDGET"
  | "LOWER_BUDGET"
  | "SET_BUDGET"
  | "INCREASE_BID_MULTIPLIER"
  | "LOWER_BID_MULTIPLIER"
  | "SET_BID_MULTIPLIER";

const ACTION_LABEL_SELECTORS: Record<ActionKey, TSelector> = {
  USE_ALGO: ($) => $.programs.action_summary["set optimal bid"],
  DO_NOTHING: ($) => $.programs.action_summary["keep current bid"],
  PAUSE: ($) => $.programs.action_summary["pause keyword or target"],
  SET_BID_CPC: ($) => $.programs.action_summary["set bid based on average CPC"],
  ADD_TAG: ($) => $.programs.action_summary["apply tag"],
  INCREASE_BID: ($) => $.programs.action_summary["increase bid"],
  LOWER_BID: ($) => $.programs.action_summary["decrease bid"],
  SET_BID: ($) => $.programs.action_summary["set bid"],
  INCREASE_BASE_BID: ($) => $.programs.action_summary["increase base bid"],
  LOWER_BASE_BID: ($) => $.programs.action_summary["decrease base bid"],
  SET_BASE_BID: ($) => $.programs.action_summary["set base bid"],
  INCREASE_PRICE: ($) => $.programs.action_summary["increase price"],
  LOWER_PRICE: ($) => $.programs.action_summary["decrease price"],
  SET_PRICE: ($) => $.programs.action_summary["set price"],
  INCREASE_BUDGET: ($) => $.programs.action_summary["increase budget"],
  LOWER_BUDGET: ($) => $.programs.action_summary["decrease budget"],
  SET_BUDGET: ($) => $.programs.action_summary["set budget"],
  INCREASE_BID_MULTIPLIER: ($) => $.programs.action_summary["increase bid multiplier"],
  LOWER_BID_MULTIPLIER: ($) => $.programs.action_summary["decrease bid multiplier"],
  SET_BID_MULTIPLIER: ($) => $.programs.action_summary["set bid multiplier"],
};

export const getActionLabel = (t: TFn, key: string): string =>
  key in ACTION_LABEL_SELECTORS ?
    t(ACTION_LABEL_SELECTORS[key as ActionKey])
  : snakeCaseToSentenceCase(key).toLowerCase();
